import React, { useState } from 'react';
import { Upload, X, Check, AlertCircle, FileText, Loader2 } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { parseGoodreadsCSV } from '../utils/goodreadsParser';
import { useAuth } from '../context/AuthContext';
import { useBooks } from '../context/BookContext';

// Normalize a title/author pair for duplicate comparison: lowercase, strip
// punctuation/whitespace so minor formatting differences ("The Hobbit" vs
// "the hobbit,") still match.
export const normalizeKey = (title, author) =>
  `${(title || '').toLowerCase().trim()}|${(author || '').toLowerCase().trim()}`
    .replace(/[^a-z0-9|]/g, '');

const ImportWizard = ({ onClose, onImportComplete }) => {
  const { user } = useAuth();
  const { books: existingBooks } = useBooks();
  const [step, setStep] = useState(1); // 1: Upload, 2: Preview, 3: Importing, 4: Success
  const [parsedBooks, setParsedBooks] = useState([]);
  const [duplicateCount, setDuplicateCount] = useState(0);
  const [skippedRowCount, setSkippedRowCount] = useState(0);
  const [stats, setStats] = useState({ total: 0, read: 0, tbr: 0, reading: 0 });
  const [importSummary, setImportSummary] = useState(null);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState(0);

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const text = event.target.result;
        const allBooks = parseGoodreadsCSV(text);

        // Rows the parser dropped outright (e.g. missing a Title column).
        const nonEmptyDataLines = text.split('\n').slice(1).filter(l => l.trim()).length;
        const droppedByParser = Math.max(0, nonEmptyDataLines - allBooks.length);

        if (allBooks.length === 0) {
          setError("No valid books found in CSV. Please check the format.");
          return;
        }

        // De-dup against the user's existing library by normalized title+author.
        const existingKeys = new Set(
          (existingBooks || []).map(b => normalizeKey(b.title, b.author))
        );
        const seenInFile = new Set();
        const books = [];
        let dupes = 0;
        allBooks.forEach(b => {
          const key = normalizeKey(b.title, b.author);
          if (existingKeys.has(key) || seenInFile.has(key)) {
            dupes++;
          } else {
            seenInFile.add(key);
            books.push(b);
          }
        });

        if (books.length === 0) {
          setError(`All ${allBooks.length} book(s) in this file are already in your library.`);
          return;
        }

        setParsedBooks(books);
        setDuplicateCount(dupes);
        setSkippedRowCount(droppedByParser);

        // Calculate stats
        const s = { total: books.length, read: 0, tbr: 0, reading: 0 };
        books.forEach(b => {
          if (b.status === 'Read') s.read++;
          else if (b.status === 'Reading') s.reading++;
          else s.tbr++;
        });
        setStats(s);
        setStep(2);
      } catch (err) {
        console.error(err);
        setError("Failed to parse file. Is it a valid CSV?");
      }
    };
    reader.readAsText(file);
  };

  const executeImport = async () => {
    setStep(3);
    const BATCH_SIZE = 50;
    const totalBatches = Math.ceil(parsedBooks.length / BATCH_SIZE);
    let added = 0;

    try {
      for (let i = 0; i < totalBatches; i++) {
        const batch = parsedBooks.slice(i * BATCH_SIZE, (i + 1) * BATCH_SIZE).map(b => ({
          user_id: user.id,
          title: b.title,
          author: b.author,
          status: b.status, // Ensure this matches DB casing if constrained, currently text is loose
          rating: b.rating,
          cover: b.cover
        }));

        const { error } = await supabase.from('books').insert(batch);
        if (error) throw error;

        added += batch.length;
        setProgress(Math.round(((i + 1) / totalBatches) * 100));
      }
      setImportSummary({ added, duplicates: duplicateCount, skipped: skippedRowCount });
      setStep(4);
      if (onImportComplete) onImportComplete();
    } catch (err) {
      console.error(err);
      setError("Import failed mid-way: " + err.message);
      setStep(2); // Go back to preview
    }
  };

  return (
    <div style={modalOverlay}>
      <div style={modalContent}>
        <button onClick={onClose} style={closeBtn}><X size={20} /></button>
        
        <h2 style={{ marginTop: 0, color: 'var(--text-main)', textAlign: 'center' }}>
          Import Library
        </h2>

        {/* STEP 1: UPLOAD */}
        {step === 1 && (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <p style={{ color: 'var(--text-soft)', marginBottom: '20px' }}>
              Upload your <b>Goodreads Export CSV</b> to migrate your library instantly.
            </p>
            
            <label style={uploadBox}>
              <Upload size={32} color="var(--accent)" />
              <div style={{ marginTop: '10px', fontWeight: 'bold', color: 'var(--text-main)' }}>
                Click to Select File
              </div>
              <div style={{ fontSize: '0.8rem', color: 'var(--text-soft)' }}>.csv files only</div>
              <input type="file" accept=".csv" onChange={handleFileUpload} style={{ display: 'none' }} />
            </label>
            
            {error && (
              <div style={errorBanner}>
                <AlertCircle size={16} /> {error}
              </div>
            )}
          </div>
        )}

        {/* STEP 2: PREVIEW */}
        {step === 2 && (
          <div>
            <div style={statGrid}>
              <div style={statBox}>
                <div style={statNum}>{stats.total}</div>
                <div style={statLabel}>Total Books</div>
              </div>
              <div style={statBox}>
                <div style={statNum}>{stats.read}</div>
                <div style={statLabel}>Read</div>
              </div>
              <div style={statBox}>
                <div style={statNum}>{stats.tbr}</div>
                <div style={statLabel}>TBR</div>
              </div>
            </div>

            <p style={{ fontSize: '0.9rem', color: 'var(--text-soft)', textAlign: 'center', margin: '20px 0' }}>
              Ready to import? This will add {stats.total} books to your Leaf & Letter library.
            </p>

            {(duplicateCount > 0 || skippedRowCount > 0) && (
              <p style={{ fontSize: '0.8rem', color: 'var(--text-soft)', textAlign: 'center', margin: '-10px 0 20px 0' }}>
                {duplicateCount > 0 && `${duplicateCount} duplicate${duplicateCount === 1 ? '' : 's'} already in your library will be skipped. `}
                {skippedRowCount > 0 && `${skippedRowCount} row${skippedRowCount === 1 ? '' : 's'} could not be read and will be skipped.`}
              </p>
            )}

            <button onClick={executeImport} style={primaryBtn}>
              Start Import
            </button>
          </div>
        )}

        {/* STEP 3: IMPORTING */}
        {step === 3 && (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <Loader2 size={48} className="spin" color="var(--accent)" />
            <h3 style={{ marginTop: '20px', color: 'var(--text-main)' }}>Importing...</h3>
            <div style={progressBarContainer}>
              <div style={{ ...progressBar, width: `${progress}%` }} />
            </div>
            <p style={{ color: 'var(--text-soft)' }}>{progress}% Complete</p>
          </div>
        )}

        {/* STEP 4: SUCCESS */}
        {step === 4 && (
          <div style={{ textAlign: 'center', padding: '20px 0' }}>
            <div style={successIcon}>
              <Check size={40} color="white" />
            </div>
            <h3 style={{ color: 'var(--text-main)', margin: '20px 0 10px 0' }}>Import Complete!</h3>
            <p style={{ color: 'var(--text-soft)', marginBottom: '30px' }}>
              {importSummary
                ? `${importSummary.added} added, ${importSummary.duplicates} duplicate${importSummary.duplicates === 1 ? '' : 's'} skipped, ${importSummary.skipped} row${importSummary.skipped === 1 ? '' : 's'} skipped.`
                : 'Your books have been added to your library.'}
            </p>
            <button onClick={onClose} style={primaryBtn}>
              Done
            </button>
          </div>
        )}
      </div>
      <style>{`
        .spin { animation: spin 1s linear infinite; }
        @keyframes spin { 100% { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
};

// Styles
const modalOverlay = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0,0,0,0.6)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 2000,
  padding: '20px'
};

const modalContent = {
  backgroundColor: 'var(--bg-card)',
  padding: '2rem',
  borderRadius: '20px',
  maxWidth: '450px',
  width: '100%',
  position: 'relative',
  boxShadow: '0 10px 25px rgba(0,0,0,0.2)'
};

const closeBtn = {
  position: 'absolute',
  top: '15px',
  right: '15px',
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  color: 'var(--text-soft)'
};

const uploadBox = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '40px',
  border: '2px dashed var(--border)',
  borderRadius: '16px',
  cursor: 'pointer',
  backgroundColor: 'var(--bg-app)',
  transition: 'all 0.2s',
  ':hover': { borderColor: 'var(--accent)' }
};

const primaryBtn = {
  width: '100%',
  padding: '14px',
  backgroundColor: 'var(--accent)',
  color: 'white',
  border: 'none',
  borderRadius: '12px',
  fontWeight: 'bold',
  cursor: 'pointer',
  fontSize: '1rem'
};

const errorBanner = {
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  padding: '10px',
  backgroundColor: '#ffebee',
  color: '#d32f2f',
  borderRadius: '8px',
  marginTop: '15px',
  fontSize: '0.9rem'
};

const statGrid = {
  display: 'flex',
  justifyContent: 'space-between',
  gap: '10px',
  marginBottom: '20px'
};

const statBox = {
  flex: 1,
  backgroundColor: 'var(--bg-app)',
  padding: '15px',
  borderRadius: '12px',
  textAlign: 'center',
  border: '1px solid var(--border)'
};

const statNum = {
  fontSize: '1.5rem',
  fontWeight: 'bold',
  color: 'var(--accent)'
};

const statLabel = {
  fontSize: '0.8rem',
  color: 'var(--text-soft)',
  marginTop: '4px'
};

const progressBarContainer = {
  width: '100%',
  height: '8px',
  backgroundColor: 'var(--bg-app)',
  borderRadius: '4px',
  overflow: 'hidden',
  margin: '20px 0 10px 0'
};

const progressBar = {
  height: '100%',
  backgroundColor: 'var(--accent)',
  transition: 'width 0.3s ease'
};

const successIcon = {
  width: '80px',
  height: '80px',
  borderRadius: '50%',
  backgroundColor: '#4caf50',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  margin: '0 auto'
};

export default ImportWizard;
